import { saveAs } from 'file-saver';
import htmlDocx from 'html-docx-js/dist/html-docx';
import JSZip from 'jszip';
import { getHeaderFooterLayoutMetrics } from './headerFooterMetrics';

const parseCssVarFromInlineStyle = (styleText, varName, fallback = '') => {
  const source = String(styleText || '');
  const escaped = String(varName || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = source.match(new RegExp(`${escaped}\\s*:\\s*([^;]+)`, 'i'));
  return match ? String(match[1] || '').trim() : fallback;
};

const normalizePxValue = (value, fallback = '0px') => {
  const raw = String(value || '').trim();
  const match = raw.match(/^([0-9]+(?:\.[0-9]+)?)px$/i);
  if (!match) return fallback;
  return `${Number(match[1])}px`;
};

const toPxNumber = (value) => {
  const match = String(value || '').trim().match(/^([0-9]+(?:\.[0-9]+)?)px$/i);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const pxToPt = (px) => {
  const n = Number(px);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `${(n * 0.75).toFixed(2)}pt`;
};

const pxToTwip = (px) => {
  const n = Number(px);
  if (!Number.isFinite(n) || n < 0) return 0;
  // 1in = 96px = 1440 twips => 1px = 15 twips
  return Math.round(n * 15);
};

const xmlEscape = (value) => {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

const hasMeaningfulHeaderFooterHtml = (value) => {
  const source = String(value || '');
  if (!source) return false;
  if (/\{\{[^{}]+\}\}/.test(source)) return true;
  if (/<(img|table|ul|ol|li|hr|svg|math)\b/i.test(source)) return true;
  const plain = source
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<\/?[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .trim();
  return plain.length > 0;
};

const detectHeaderFooterTypography = (html) => {
  const source = String(html || '');
  if (!source) return { fontFamily: '', fontSizeHalfPt: null };

  const styleMatches = Array.from(source.matchAll(/style\s*=\s*["']([^"']+)["']/gi));
  let fontFamily = '';
  let fontSizeHalfPt = null;

  const toHalfPt = (raw) => {
    const m = String(raw || '').trim().match(/^([0-9]+(?:\.[0-9]+)?)\s*(pt|px)?$/i);
    if (!m) return null;
    const n = Number(m[1]);
    if (!Number.isFinite(n) || n <= 0) return null;
    const unit = String(m[2] || 'pt').toLowerCase();
    const pt = unit === 'px' ? (n * 0.75) : n;
    return Math.max(2, Math.round(pt * 2));
  };

  for (const match of styleMatches) {
    const styleText = String(match[1] || '');
    if (!fontFamily) {
      const ff = styleText.match(/font-family\s*:\s*([^;]+)/i);
      if (ff && ff[1]) {
        const first = String(ff[1]).split(',')[0].replace(/["']/g, '').trim();
        if (first) fontFamily = first;
      }
    }
    if (fontSizeHalfPt === null) {
      const fs = styleText.match(/font-size\s*:\s*([^;]+)/i);
      if (fs && fs[1]) {
        const hp = toHalfPt(fs[1]);
        if (hp !== null) fontSizeHalfPt = hp;
      }
    }
    if (fontFamily && fontSizeHalfPt !== null) break;
  }

  return { fontFamily, fontSizeHalfPt };
};

const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TR', 'TD', 'TH']);

const parseStyleMap = (styleText) => {
  const out = {};
  String(styleText || '')
    .split(';')
    .forEach((part) => {
      const [k, v] = String(part || '').split(':');
      const key = String(k || '').trim().toLowerCase();
      const val = String(v || '').trim();
      if (key && val) out[key] = val;
    });
  return out;
};

const parseFontSizeToHalfPt = (raw) => {
  const m = String(raw || '').trim().match(/^([0-9]+(?:\.[0-9]+)?)\s*(pt|px)?$/i);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  const unit = String(m[2] || 'pt').toLowerCase();
  const pt = unit === 'px' ? (n * 0.75) : n;
  return Math.max(2, Math.round(pt * 2));
};

const parseFontWeightBold = (raw) => {
  const value = String(raw || '').trim().toLowerCase();
  if (!value) return null;
  if (value === 'bold' || value === 'bolder') return true;
  const num = Number(value);
  if (Number.isFinite(num)) return num >= 600;
  return null;
};

const parseTextAlign = (el) => {
  const style = parseStyleMap(el?.getAttribute?.('style') || '');
  const alignRaw = String(style['text-align'] || '').trim().toLowerCase();
  if (alignRaw === 'left' || alignRaw === 'center' || alignRaw === 'right' || alignRaw === 'justify') return alignRaw;
  const cls = String(el?.getAttribute?.('class') || '');
  if (/\bhas-text-align-center\b/.test(cls)) return 'center';
  if (/\bhas-text-align-right\b/.test(cls)) return 'right';
  if (/\bhas-text-align-justify\b/.test(cls)) return 'justify';
  if (/\bhas-text-align-left\b/.test(cls)) return 'left';
  return null;
};

const mergeRunStyle = (base, patch) => {
  const out = { ...(base || {}) };
  Object.keys(patch || {}).forEach((k) => {
    const v = patch[k];
    if (v !== null && v !== undefined && v !== '') out[k] = v;
  });
  return out;
};

const getRunStyleFromElement = (el) => {
  const tag = String(el?.tagName || '').toUpperCase();
  const styleMap = parseStyleMap(el?.getAttribute?.('style') || '');
  let fontFamily = '';
  const ff = String(styleMap['font-family'] || '').trim();
  if (ff) {
    const first = ff.split(',')[0].replace(/["']/g, '').trim();
    if (first) fontFamily = first;
  }
  const fontSizeHalfPt = parseFontSizeToHalfPt(styleMap['font-size']);
  const boldFromWeight = parseFontWeightBold(styleMap['font-weight']);
  const italicFromStyle = String(styleMap['font-style'] || '').trim().toLowerCase() === 'italic' ? true : null;
  const td = String(styleMap['text-decoration'] || '').toLowerCase();
  const underlineFromStyle = td.includes('underline') ? true : null;

  return {
    fontFamily: fontFamily || null,
    fontSizeHalfPt: fontSizeHalfPt ?? null,
    bold: (tag === 'B' || tag === 'STRONG') ? true : boldFromWeight,
    italic: (tag === 'I' || tag === 'EM') ? true : italicFromStyle,
    underline: tag === 'U' ? true : underlineFromStyle,
  };
};

const extractHeaderFooterParagraphs = (html, defaults = {}) => {
  const source = String(html || '').trim();
  if (!source) return [];
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return source
      .split(/\r?\n/)
      .map((line) => ({ align: null, runs: [{ text: line, style: defaults }] }))
      .filter((p) => String(p.runs?.[0]?.text || '').trim() !== '');
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div id="hf-root">${source}</div>`, 'text/html');
  const root = doc.getElementById('hf-root');
  if (!root) return [];

  const paragraphs = [];

  const appendRunsFromNode = (node, inheritedStyle, targetRuns) => {
    if (!node) return;
    if (node.nodeType === 3) {
      const text = String(node.nodeValue || '').replace(/\u00A0/g, ' ');
      if (text) targetRuns.push({ text, style: inheritedStyle });
      return;
    }
    if (node.nodeType !== 1) return;
    const el = node;
    if (String(el.tagName || '').toUpperCase() === 'BR') {
      targetRuns.push({ break: true, style: inheritedStyle });
      return;
    }
    const style = mergeRunStyle(inheritedStyle, getRunStyleFromElement(el));
    Array.from(el.childNodes || []).forEach((child) => appendRunsFromNode(child, style, targetRuns));
  };

  const pushParagraphFromElement = (el) => {
    const runs = [];
    const baseStyle = mergeRunStyle({}, defaults);
    appendRunsFromNode(el, baseStyle, runs);
    if (runs.length === 0) runs.push({ text: '', style: baseStyle });
    paragraphs.push({
      align: parseTextAlign(el),
      runs,
    });
  };

  Array.from(root.childNodes || []).forEach((node) => {
    if (!node) return;
    if (node.nodeType === 3) {
      const text = String(node.nodeValue || '').replace(/\u00A0/g, ' ');
      if (text.trim()) {
        paragraphs.push({ align: null, runs: [{ text, style: defaults }] });
      }
      return;
    }
    if (node.nodeType !== 1) return;
    const el = node;
    if (BLOCK_TAGS.has(String(el.tagName || '').toUpperCase())) {
      pushParagraphFromElement(el);
      return;
    }
    // inline root content -> один абзац
    pushParagraphFromElement(el);
  });

  return paragraphs;
};

const buildHeaderFooterPartXml = (html, partType = 'header') => {
  const tag = partType === 'footer' ? 'ftr' : 'hdr';
  const layoutMetrics = getHeaderFooterLayoutMetrics(html);
  const { fontFamily, fontSizeHalfPt } = detectHeaderFooterTypography(html);
  const defaultRunStyle = {
    fontFamily: fontFamily || null,
    fontSizeHalfPt: fontSizeHalfPt ?? null,
    bold: null,
    italic: null,
    underline: null,
  };
  const paragraphs = extractHeaderFooterParagraphs(html, defaultRunStyle);
  const lineTwips = pxToTwip(layoutMetrics?.lineHeightPx || 0);
  const spacingAttrs = lineTwips > 0
    ? `w:before="0" w:after="0" w:line="${lineTwips}" w:lineRule="exact"`
    : 'w:before="0" w:after="0"';
  const toRunPropsXml = (style) => {
    const s = style || {};
    const props = [];
    if (s.fontFamily) {
      const escapedFont = xmlEscape(s.fontFamily);
      props.push(`<w:rFonts w:ascii="${escapedFont}" w:hAnsi="${escapedFont}" w:cs="${escapedFont}"/>`);
    }
    if (Number.isFinite(Number(s.fontSizeHalfPt)) && Number(s.fontSizeHalfPt) > 0) {
      const val = Math.round(Number(s.fontSizeHalfPt));
      props.push(`<w:sz w:val="${val}"/><w:szCs w:val="${val}"/>`);
    }
    if (s.bold === true) props.push('<w:b/>');
    if (s.italic === true) props.push('<w:i/>');
    if (s.underline === true) props.push('<w:u w:val="single"/>');
    return props.length > 0 ? `<w:rPr>${props.join('')}</w:rPr>` : '';
  };

  const toParagraphAlignXml = (align) => {
    const a = String(align || '').toLowerCase();
    if (a === 'left') return '<w:jc w:val="left"/>';
    if (a === 'center') return '<w:jc w:val="center"/>';
    if (a === 'right') return '<w:jc w:val="right"/>';
    if (a === 'justify') return '<w:jc w:val="both"/>';
    return '';
  };

  const bodyXml = (paragraphs.length > 0 ? paragraphs : [{ align: null, runs: [{ text: '', style: defaultRunStyle }] }])
    .map((paragraph) => {
      const runs = Array.isArray(paragraph.runs) && paragraph.runs.length > 0
        ? paragraph.runs
        : [{ text: '', style: defaultRunStyle }];
      const runsXml = runs.map((run) => {
        const runPropsXml = toRunPropsXml(run.style || defaultRunStyle);
        if (run.break) return `<w:r>${runPropsXml}<w:br/></w:r>`;
        return `<w:r>${runPropsXml}<w:t xml:space="preserve">${xmlEscape(run.text || '')}</w:t></w:r>`;
      }).join('');
      const alignXml = toParagraphAlignXml(paragraph.align);
      return `<w:p><w:pPr><w:spacing ${spacingAttrs}/>${alignXml}</w:pPr>${runsXml}</w:p>`;
    })
    .join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:${tag} xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
${bodyXml}
</w:${tag}>`;
};

const addRelationship = (relsXml, type, target, id) => {
  const rel = `<Relationship Id="${id}" Type="${type}" Target="${target}"/>`;
  if (relsXml.includes('</Relationships>')) {
    return relsXml.replace('</Relationships>', `${rel}</Relationships>`);
  }
  return relsXml;
};

const addContentTypeOverride = (contentTypesXml, partName, contentType) => {
  const exists = new RegExp(`PartName=["']${partName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']`, 'i').test(contentTypesXml);
  if (exists) return contentTypesXml;
  const override = `<Override PartName="${partName}" ContentType="${contentType}"/>`;
  if (contentTypesXml.includes('</Types>')) {
    return contentTypesXml.replace('</Types>', `${override}</Types>`);
  }
  return contentTypesXml;
};

const injectHeaderFooterParts = async (docxBlob, headerHtml, footerHtml) => {
  const hasHeader = !!String(headerHtml || '').trim();
  const hasFooter = !!String(footerHtml || '').trim();

  const zip = await JSZip.loadAsync(docxBlob);
  const documentFile = zip.file('word/document.xml');
  const relsFile = zip.file('word/_rels/document.xml.rels');
  const typesFile = zip.file('[Content_Types].xml');
  if (!documentFile) return docxBlob;

  let documentXml = await documentFile.async('string');
  let relsXml = relsFile ? await relsFile.async('string') : '';
  let contentTypesXml = typesFile ? await typesFile.async('string') : '';

  let headerRefXml = '';
  let footerRefXml = '';

  if (hasHeader && relsFile && typesFile) {
    const headerId = 'rIdAotHeader1';
    zip.file('word/header1.xml', buildHeaderFooterPartXml(headerHtml, 'header'));
    relsXml = addRelationship(
      relsXml,
      'http://schemas.openxmlformats.org/officeDocument/2006/relationships/header',
      'header1.xml',
      headerId
    );
    contentTypesXml = addContentTypeOverride(
      contentTypesXml,
      '/word/header1.xml',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml'
    );
    headerRefXml = `<w:headerReference w:type="default" r:id="${headerId}"/>`;
  }

  if (hasFooter && relsFile && typesFile) {
    const footerId = 'rIdAotFooter1';
    zip.file('word/footer1.xml', buildHeaderFooterPartXml(footerHtml, 'footer'));
    relsXml = addRelationship(
      relsXml,
      'http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer',
      'footer1.xml',
      footerId
    );
    contentTypesXml = addContentTypeOverride(
      contentTypesXml,
      '/word/footer1.xml',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml'
    );
    footerRefXml = `<w:footerReference w:type="default" r:id="${footerId}"/>`;
  }

  if ((hasHeader || hasFooter) && relsFile && typesFile) {
    // Удаляем возможные старые ссылки, чтобы не дублировать
    documentXml = documentXml
      .replace(/<w:headerReference[^>]*\/>/g, '')
      .replace(/<w:footerReference[^>]*\/>/g, '');

    // Вставляем references внутрь sectPr (до pgSz/pgMar допустимо)
    documentXml = documentXml.replace(/<w:sectPr>/, `<w:sectPr>${headerRefXml}${footerRefXml}`);
  }

  // Принудительно фиксируем формат страницы A4 в twips:
  // 210mm x 297mm => 11906 x 16838.
  // Иначе html-docx-js в ряде случаев оставляет дефолт US Letter.
  const a4PgSz = '<w:pgSz w:w="11906" w:h="16838" w:orient="portrait"/>';
  if (/<w:pgSz\b[^>]*\/>/.test(documentXml)) {
    documentXml = documentXml.replace(/<w:pgSz\b[^>]*\/>/, a4PgSz);
  } else if (/<w:sectPr>/.test(documentXml)) {
    documentXml = documentXml.replace(/<w:sectPr>/, `<w:sectPr>${a4PgSz}`);
  }

  zip.file('word/document.xml', documentXml);
  if (relsFile && relsXml) {
    zip.file('word/_rels/document.xml.rels', relsXml);
  }
  if (typesFile && contentTypesXml) {
    zip.file('[Content_Types].xml', contentTypesXml);
  }

  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

const deriveColWidthsFromCells = (tableEl) => {
  const colWidths = [];
  const rows = Array.from(tableEl.querySelectorAll('tr'));
  rows.forEach((row) => {
    let colIndex = 0;
    Array.from(row.children).forEach((cell) => {
      if (!cell || (cell.tagName !== 'TD' && cell.tagName !== 'TH')) return;
      const span = Math.max(1, Number(cell.getAttribute('colspan') || 1));
      const colwidthRaw = String(cell.getAttribute('colwidth') || '').trim();
      const rawParts = colwidthRaw
        .split(',')
        .map((part) => String(part || '').trim())
        .filter(Boolean);
      const parsedParts = rawParts.map((part) => {
        const n = Number(part);
        return Number.isFinite(n) && n > 0 ? n : null;
      });

      for (let i = 0; i < span; i += 1) {
        const idx = colIndex + i;
        let candidate = null;
        if (parsedParts.length > 1) {
          candidate = parsedParts[i] ?? null;
        } else if (parsedParts.length === 1) {
          candidate = parsedParts[0];
        }
        if (candidate && !colWidths[idx]) {
          colWidths[idx] = `${candidate}px`;
        }
      }
      colIndex += span;
    });
  });
  return colWidths.filter(Boolean).length > 0 ? colWidths : [];
};

const detectTableColumnCount = (tableEl) => {
  const rows = Array.from(tableEl.querySelectorAll('tr'));
  let maxCols = 0;
  rows.forEach((row) => {
    const count = Array.from(row.children).reduce((sum, cell) => {
      if (!cell || (cell.tagName !== 'TD' && cell.tagName !== 'TH')) return sum;
      const span = Math.max(1, Number(cell.getAttribute('colspan') || 1));
      return sum + span;
    }, 0);
    if (count > maxCols) maxCols = count;
  });
  return maxCols;
};

const preprocessHtmlForDocx = (html, options = {}) => {
  const source = String(html || '');
  if (!source) return source;
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return source;
  }
  const pageContentWidthPt = Number(options?.pageContentWidthPt);

  try {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(`<div id="docx-root">${source}</div>`, 'text/html');
    const root = doc.getElementById('docx-root');
    if (!root) return source;

    // В редакторе часть пустых строк визуализируется через CSS (:empty::after),
    // но в DOCX это теряется. Конвертируем такие <p> в реальные непустые абзацы.
    root.querySelectorAll('p').forEach((p) => {
      const text = String(p.textContent || '').replace(/\u00A0/g, ' ').trim();
      const hasMedia = p.querySelector('img,svg,table,iframe,object,embed');
      const onlyBreaks = Array.from(p.childNodes).every((node) => {
        if (node.nodeType === 3) {
          return String(node.nodeValue || '').replace(/\u00A0/g, ' ').trim() === '';
        }
        if (node.nodeType === 1) {
          return node.nodeName === 'BR';
        }
        return true;
      });
      const markedEmpty = p.getAttribute('data-empty') === 'true';
      const isVisualEmpty = !hasMedia && (markedEmpty || (text === '' && (p.childNodes.length === 0 || onlyBreaks)));
      if (isVisualEmpty) {
        p.innerHTML = '&nbsp;';
      }
    });

    const tables = root.querySelectorAll('table');
    const hasMeaningfulPrevSibling = (el) => {
      let node = el?.previousSibling || null;
      while (node) {
        if (node.nodeType === 3) {
          const text = String(node.nodeValue || '').replace(/\u00A0/g, ' ').trim();
          if (text) return true;
          node = node.previousSibling;
          continue;
        }
        if (node.nodeType === 1) {
          const tag = String(node.nodeName || '').toUpperCase();
          if (tag === 'P') {
            const txt = String(node.textContent || '').replace(/\u00A0/g, ' ').trim();
            if (txt) return true;
          } else if (tag === 'BR') {
            // ignore
          } else {
            return true;
          }
        }
        node = node.previousSibling;
      }
      return false;
    };
    const hasMeaningfulNextSibling = (el) => {
      let node = el?.nextSibling || null;
      while (node) {
        if (node.nodeType === 3) {
          const text = String(node.nodeValue || '').replace(/\u00A0/g, ' ').trim();
          if (text) return true;
          node = node.nextSibling;
          continue;
        }
        if (node.nodeType === 1) {
          const tag = String(node.nodeName || '').toUpperCase();
          if (tag === 'P') {
            const txt = String(node.textContent || '').replace(/\u00A0/g, ' ').trim();
            if (txt) return true;
          } else if (tag === 'BR') {
            // ignore
          } else {
            return true;
          }
        }
        node = node.nextSibling;
      }
      return false;
    };
    tables.forEach((tableEl) => {
      const tableStyle = tableEl.getAttribute('style') || '';
      const borderWidth = normalizePxValue(parseCssVarFromInlineStyle(tableStyle, '--table-border-width', '1px'), '1px');
      const rowMinHeight = normalizePxValue(parseCssVarFromInlineStyle(tableStyle, '--table-row-min-height', '0px'), '0px');
      const tableMargin = normalizePxValue(parseCssVarFromInlineStyle(tableStyle, '--table-margin-vert', '15px'), '15px');
      const cellPadding = normalizePxValue(parseCssVarFromInlineStyle(tableStyle, '--table-cell-padding', '8px'), '8px');
      const borderColor = parseCssVarFromInlineStyle(tableStyle, '--table-border-color', '#000') || '#000';

      tableEl.style.borderCollapse = 'collapse';
      tableEl.style.borderSpacing = '0';
      tableEl.style.tableLayout = 'fixed';
      tableEl.style.setProperty('mso-table-layout-alt', 'fixed');
      if (!tableEl.style.width) {
        tableEl.style.width = '100%';
      }
      tableEl.style.border = `${borderWidth} solid ${borderColor}`;
      tableEl.style.margin = `${tableMargin} 0`;

      const tableMarginNum = Number(String(tableMargin).replace('px', '').trim());
      if (Number.isFinite(tableMarginNum) && tableMarginNum > 0) {
        const gapPt = (tableMarginNum * 0.75).toFixed(2);
        // Важно: не вставляем верхний spacer перед первой таблицей документа.
        // Иначе на 1-й странице появляется дополнительный зазор от header до текста.
        if (hasMeaningfulPrevSibling(tableEl)) {
          const before = doc.createElement('p');
          before.innerHTML = '&nbsp;';
          before.setAttribute('style', `margin: 0; line-height: ${gapPt}pt; font-size: 1pt;`);
          tableEl.parentNode?.insertBefore(before, tableEl);
        }
        if (hasMeaningfulNextSibling(tableEl)) {
          const after = doc.createElement('p');
          after.innerHTML = '&nbsp;';
          after.setAttribute('style', `margin: 0; line-height: ${gapPt}pt; font-size: 1pt;`);
          if (tableEl.nextSibling) {
            tableEl.parentNode?.insertBefore(after, tableEl.nextSibling);
          } else {
            tableEl.parentNode?.appendChild(after);
          }
        }
      }

      // Переносим ширины колонок из colgroup/col в явные ширины ячеек,
      // т.к. Word часто игнорирует/переинтерпретирует только colgroup.
      let colWidths = Array.from(tableEl.querySelectorAll('colgroup col')).map((col) => {
        const directWidth = String(col.getAttribute('width') || '').trim();
        const styleWidth = String(col.style?.width || '').trim();
        const raw = styleWidth || directWidth;
        const px = normalizePxValue(raw, '');
        if (px) return px;
        const numeric = String(raw).match(/^([0-9]+(?:\.[0-9]+)?)$/);
        return numeric ? `${Number(numeric[1])}px` : '';
      });
      // Если colgroup задан не полностью (часть колонок без ширины),
      // аккуратно добираем только отсутствующие значения из colwidth ячеек.
      const derivedColWidths = deriveColWidthsFromCells(tableEl);
      if (colWidths.filter(Boolean).length === 0) {
        colWidths = derivedColWidths;
      } else if (derivedColWidths.length > 0) {
        const maxLen = Math.max(colWidths.length, derivedColWidths.length);
        const merged = new Array(maxLen).fill('');
        for (let i = 0; i < maxLen; i += 1) {
          merged[i] = colWidths[i] || derivedColWidths[i] || '';
        }
        colWidths = merged;
      }

      let numericColWidths = colWidths.map((value) => toPxNumber(value));
      if (colWidths.length > 0) {
        const totalWidth = numericColWidths.reduce((sum, item) => {
          return sum + (Number.isFinite(item) && item > 0 ? item : 0);
        }, 0);
        if (totalWidth > 0) {
          // Сохраняем полную ширину таблицы, чтобы она не сжималась в Word.
          tableEl.style.width = '100%';
          tableEl.setAttribute('width', '100%');

          // Явный colgroup: фиксированные колонки задаем в px + width-атрибут,
          // пустые/нулевые оставляем auto, чтобы занимали остаток ширины.
          tableEl.querySelectorAll(':scope > colgroup').forEach((el) => el.remove());
          const colgroup = doc.createElement('colgroup');
          numericColWidths.forEach((width) => {
            const col = doc.createElement('col');
            const n = Number(width);
            if (Number.isFinite(n) && n > 0) {
              const widthPt = pxToPt(n);
              if (widthPt) {
                col.style.width = widthPt;
              }
              col.setAttribute('width', String(Math.round(n)));
            }
            colgroup.appendChild(col);
          });
          tableEl.insertBefore(colgroup, tableEl.firstChild);
        }
      }

      // Fallback: если явные ширины колонок отсутствуют, задаем равные доли.
      const knownCols = numericColWidths.filter((n) => Number.isFinite(n) && n > 0).length;
      if (knownCols === 0) {
        const colCount = detectTableColumnCount(tableEl);
        if (colCount > 1) {
          const fallbackTotalPt = Number.isFinite(pageContentWidthPt) && pageContentWidthPt > 0
            ? pageContentWidthPt
            : 500;
          const fallbackColPt = fallbackTotalPt / colCount;
          const fallbackColPx = (fallbackColPt / 0.75);
          numericColWidths = new Array(colCount).fill(fallbackColPx);
          tableEl.querySelectorAll(':scope > colgroup').forEach((el) => el.remove());
          const colgroup = doc.createElement('colgroup');
          for (let i = 0; i < colCount; i += 1) {
            const col = doc.createElement('col');
            const widthPt = pxToPt(fallbackColPx);
            if (widthPt) {
              col.style.width = widthPt;
              col.setAttribute('width', widthPt);
            }
            colgroup.appendChild(col);
          }
          tableEl.insertBefore(colgroup, tableEl.firstChild);
          tableEl.style.width = '100%';
          tableEl.setAttribute('width', '100%');
        }
      }

      // Для Word дополнительно фиксируем ширины ячеек по логическим колонкам,
      // учитывая объединения строк/столбцов.
      if (numericColWidths.some((n) => Number.isFinite(n) && n > 0)) {
        const pendingRowSpans = [];
        tableEl.querySelectorAll('tr').forEach((row) => {
          let colIndex = 0;
          Array.from(row.children).forEach((cell) => {
            if (!cell || (cell.tagName !== 'TD' && cell.tagName !== 'TH')) return;
            while (pendingRowSpans[colIndex] > 0) {
              pendingRowSpans[colIndex] -= 1;
              colIndex += 1;
            }

            const span = Math.max(1, Number(cell.getAttribute('colspan') || 1));
            const rowSpan = Math.max(1, Number(cell.getAttribute('rowspan') || 1));
            const widthsSlice = numericColWidths.slice(colIndex, colIndex + span);
            const allKnown = widthsSlice.length > 0 && widthsSlice.every((n) => Number.isFinite(n) && n > 0);
            if (allKnown) {
              const sumPx = widthsSlice.reduce((acc, n) => acc + n, 0);
              const widthPt = pxToPt(sumPx);
              if (widthPt) {
                cell.style.width = widthPt;
              }
              cell.setAttribute('width', String(Math.round(sumPx)));
            }

            for (let i = 0; i < span; i += 1) {
              const idx = colIndex + i;
              if (rowSpan > 1) {
                pendingRowSpans[idx] = (pendingRowSpans[idx] || 0) + (rowSpan - 1);
              }
            }
            colIndex += span;
          });
        });
      }

      const borderless = borderWidth === '0px';
      tableEl.removeAttribute('border');
      if (borderless) {
        tableEl.style.border = 'none';
      }

      tableEl.querySelectorAll('td,th').forEach((cell) => {
        cell.removeAttribute('border');
        if (borderless) {
          cell.style.border = 'none';
        } else {
          cell.style.border = `${borderWidth} solid ${borderColor}`;
        }
        cell.style.verticalAlign = cell.style.verticalAlign || 'top';
        if (!cell.style.padding) {
          cell.style.padding = cellPadding;
        }
        if (rowMinHeight !== '0px') {
          cell.style.height = rowMinHeight;
        } else if (cell.style.height) {
          cell.style.removeProperty('height');
        }
      });

      tableEl.querySelectorAll('th').forEach((th) => {
        th.style.backgroundColor = 'transparent';
        if (!th.style.textAlign) {
          th.style.textAlign = 'left';
        }
      });
    });

    return root.innerHTML;
  } catch (error) {
    console.error('Ошибка подготовки HTML для DOCX:', error);
    return source;
  }
};

/**
 * Экспортирует HTML контент в DOCX файл с сохранением форматирования
 * @param {string} htmlContent - HTML контент для экспорта
 * @param {string} filename - Имя файла (без расширения)
 * @param {Object} margins - Поля страницы {top, right, bottom, left} в пикселях
 */
export const exportToDocx = async (htmlContent, filename = 'document', margins = {}, options = {}) => {
  try {
    const marginsPx = {
      top: Number(margins?.top ?? 30),
      right: Number(margins?.right ?? 30),
      bottom: Number(margins?.bottom ?? 30),
      left: Number(margins?.left ?? 30),
    };
    const rawHeaderContent = String(options?.headerContent ?? options?.header_content ?? '').trim();
    const rawFooterContent = String(options?.footerContent ?? options?.footer_content ?? '').trim();
    const headerGapPx = Math.max(0, Number(options?.headerGapPx ?? options?.header_gap_px) || 0);
    const footerGapPx = Math.max(0, Number(options?.footerGapPx ?? options?.footer_gap_px) || 0);
    const explicitEffectiveHeaderGapPx = Number(
      options?.effectiveHeaderGapPx ?? options?.effective_header_gap_px
    );
    const headerMetrics = getHeaderFooterLayoutMetrics(rawHeaderContent);
    const estimatedHeaderHeightPx = Math.max(0, Math.ceil(Number(headerMetrics?.blockHeightPx) || 0));
    const effectiveHeaderGapPx = Number.isFinite(explicitEffectiveHeaderGapPx)
      ? Math.max(0, explicitEffectiveHeaderGapPx)
      : (headerGapPx > 0 ? headerGapPx : Math.max(headerGapPx, estimatedHeaderHeightPx));
    // Верх/низ "тела" в DOCX должны совпадать с границами в редакторе:
    // top = верхнее поле + отступ до начала body
    // bottom = нижнее поле + отступ от конца body до footer.
    const contentMarginsPx = {
      top: marginsPx.top + effectiveHeaderGapPx,
      right: marginsPx.right,
      bottom: marginsPx.bottom + footerGapPx,
      left: marginsPx.left,
    };
    // Преобразуем margins из пикселей в точки (1 пиксель = 0.75 точки @96dpi)
    const marginsInPt = {
      top: contentMarginsPx.top * 0.75,
      right: contentMarginsPx.right * 0.75,
      bottom: contentMarginsPx.bottom * 0.75,
      left: contentMarginsPx.left * 0.75,
    };
    const pageContentWidthPt = 595.28 - marginsInPt.left - marginsInPt.right;
    const preparedHtmlContent = preprocessHtmlForDocx(htmlContent, { pageContentWidthPt });
    const hasHeaderContent = hasMeaningfulHeaderFooterHtml(rawHeaderContent);
    const hasFooterContent = hasMeaningfulHeaderFooterHtml(rawFooterContent);
    const preparedHeaderContent = hasHeaderContent
      ? preprocessHtmlForDocx(rawHeaderContent, { pageContentWidthPt })
      : '';
    const preparedFooterContent = hasFooterContent
      ? preprocessHtmlForDocx(rawFooterContent, { pageContentWidthPt })
      : '';

    // Создаем полный HTML документ с правильными стилями
    const fullHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          @page {
            size: A4;
            margin: ${marginsInPt.top}pt ${marginsInPt.right}pt ${marginsInPt.bottom}pt ${marginsInPt.left}pt;
          }
          body {
            line-height: 1.15;
            margin: 0;
            padding: 0;
          }
          p {
            margin: 0;
            line-height: inherit;
          }
          h1 {
            font-size: 24pt;
            font-weight: bold;
            margin: 20pt 0 15pt;
          }
          h2 {
            font-size: 18pt;
            font-weight: bold;
            margin: 18pt 0 12pt;
          }
          h3 {
            font-size: 14.4pt;
            font-weight: bold;
            margin: 16pt 0 10pt;
          }
          table { border-collapse: collapse; border-spacing: 0; }
          table td, table th { box-sizing: border-box; }
          table th {
            text-align: left;
            background-color: transparent;
          }
          ul, ol {
            padding-left: 24pt;
            margin: 10pt 0;
          }
          li {
            margin: 5pt 0;
          }
          .has-text-align-left { text-align: left; }
          .has-text-align-center { text-align: center; }
          .has-text-align-right { text-align: right; }
          .has-text-align-justify { text-align: justify; }
          strong { font-weight: bold; }
          em { font-style: italic; }
          u { text-decoration: underline; }
          s { text-decoration: line-through; }
        </style>
      </head>
      <body>
        ${preparedHtmlContent}
      </body>
      </html>
    `;

    // Конвертируем HTML в DOCX
    const baseBlob = htmlDocx.asBlob(fullHtml, {
      margins: {
        top: pxToTwip(contentMarginsPx.top),
        right: pxToTwip(contentMarginsPx.right),
        bottom: pxToTwip(contentMarginsPx.bottom),
        left: pxToTwip(contentMarginsPx.left),
        // Расстояние от края страницы до header/footer фиксируем по полям шаблона.
        header: pxToTwip(marginsPx.top),
        footer: pxToTwip(marginsPx.bottom),
        gutter: 0,
      },
    });
    const converted = await injectHeaderFooterParts(baseBlob, preparedHeaderContent, preparedFooterContent);

    // Сохраняем файл
    saveAs(converted, `${filename}.docx`);

    return true;
  } catch (error) {
    console.error('Ошибка при экспорте в DOCX:', error);
    alert('Произошла ошибка при экспорте документа');
    return false;
  }
};

/**
 * Извлекает чистый HTML контент из страниц предпросмотра
 * @param {HTMLElement} pagesContainer - Контейнер со страницами предпросмотра
 * @returns {string} Чистый HTML контент
 */
export const extractContentFromPages = (pagesContainer) => {
  if (!pagesContainer) return '';

  const pages = pagesContainer.querySelectorAll('.a4-preview-content');
  if (!pages.length) return '';

  // Собираем контент со всех страниц
  let combinedContent = '';
  
  pages.forEach((page, index) => {
    if (index > 0) {
      // Добавляем разрыв страницы между страницами
      combinedContent += '<div style="page-break-before: always;"></div>';
    }
    
    // Получаем HTML контент страницы
    let pageContent = page.innerHTML;
    
    // Очищаем от служебных атрибутов и классов
    pageContent = pageContent
      .replace(/data-empty="true"/g, '')
      .replace(/class="[^"]*"/g, (match) => {
        // Оставляем только значимые классы для форматирования
        const classes = match.match(/class="([^"]*)"/)[1]
          .split(' ')
          .filter(cls => cls.startsWith('has-text-align-'))
          .join(' ');
        return classes ? `class="${classes}"` : '';
      });
    
    combinedContent += pageContent;
  });

  return combinedContent;
};
